/**
 * Anthropometry (de Leva 1996) + joint strength capacities.
 * These numbers are duplicated in the Python estimator (acro_movement/biomech/segments.py);
 * a parity test keeps both tables in sync.
 */
import type { CanonicalPoint, JointId, SegmentId, Vec3 } from "./types.ts";

export const GRAVITY: Vec3 = [0, -9.81, 0];
export const DEFAULT_MASS_KG = 70;
export const DEFAULT_HEIGHT_M = 1.72;
/** BMI used to derive mass from height when mass is unknown. */
export const ASSUMED_BMI = 22;

/** Hands/feet floor allowance (kept for API compatibility; see GROUND_SEGMENTS). */
export const GROUND_CONTACT_DISTANCE_M = 0.06;
/** Speed gate (m/s) for floor contacts and resting floor samples. */
export const GROUND_CONTACT_SPEED_MS = 0.6;
/** Person-to-person contact detection (distal point to other person's segment). */
export const PERSON_CONTACT_DISTANCE_M = 0.1;
/** Two detections of the same (supported, supporting) pair closer than this are one contact. */
export const PERSON_DEDUPE_M = 0.15;
/**
 * A segment must be this much closer than an earlier (more proximal) one to win the
 * closest-segment search: a load at the hip/shoulder girdle belongs to the trunk.
 */
export const SEGMENT_TIE_M = 0.02;
/** Largest force one person contact may carry, in body weights of the supported person. */
export const PERSON_FORCE_CAP_BW = 3.0;
/** Contacts are weighted by 1 / (horizontal distance to the load centre + this). */
export const CONTACT_WEIGHT_EPS_M = 0.05;
/** Redistribution passes after clamping contacts. */
export const DISTRIBUTION_PASSES = 3;
/** Coulomb friction coefficient used to clamp ground contact tangential force. */
export const FRICTION_MU = 0.8;
/** Floor penetration tolerance for validation (metres). */
export const FLOOR_PENETRATION_M = 0.05;
/** Lying/sitting trunk surface sits this far below the mid_hip/mid_shoulder landmarks. */
export const TRUNK_FLOOR_OFFSET_M = 0.1;

/* ---- validation (mirrors acro_movement/biomech/validation.py) */

/** Airborne consistency tolerance on |com acc - g| (m/s²). */
export const AIRBORNE_TOLERANCE = 3.5;
/** Frames at each end of a valid run / contact-free run that are not scored. */
export const EDGE_FRAMES = 3;
/** Residual vectors are averaged over this centred window (s) before scoring. */
export const SUSTAIN_WINDOW_S = 0.3;
export const RESIDUAL_FORCE_FREE_BW = 0.1;
export const RESIDUAL_FORCE_PENALTY = 200;
export const RESIDUAL_MOMENT_FREE_BWH = 0.04;
export const RESIDUAL_MOMENT_PENALTY = 600;
export const RESIDUAL_FINDING_BW = 0.25;
export const RESIDUAL_ERROR_BW = 0.5;
export const RESIDUAL_MOMENT_FINDING_BWH = 0.08;
export const RESIDUAL_MOMENT_ERROR_BWH = 0.16;
export const OVERLOAD_PENALTY = 50;
export const AIRBORNE_PENALTY = 60;
export const FRICTION_PENALTY = 30;
export const PENETRATION_PENALTY = 30;
export const CONTACT_CAP_PENALTY = 30;
export const LOW_CONFIDENCE = 0.5;
export const LOW_CONFIDENCE_SCORE = 40;
export const PASS_SCORE = 75;
export const WARN_SCORE = 50;
export const MAX_FINDINGS = 25;

/** Ordered canonical joints of a BiomechTrack. */
export const JOINTS: JointId[] = [
  "neck",
  "shoulder_l",
  "shoulder_r",
  "elbow_l",
  "elbow_r",
  "wrist_l",
  "wrist_r",
  "hip_l",
  "hip_r",
  "knee_l",
  "knee_r",
  "ankle_l",
  "ankle_r",
];

/** Peak isometric joint moment capacity, N·m per kg of body mass (conservative adult). */
export const JOINT_CAPACITY_NM_PER_KG: Record<JointId, number> = {
  neck: 1.0,
  shoulder_l: 1.3,
  shoulder_r: 1.3,
  elbow_l: 0.9,
  elbow_r: 0.9,
  wrist_l: 0.25,
  wrist_r: 0.25,
  hip_l: 3.0,
  hip_r: 3.0,
  knee_l: 3.0,
  knee_r: 3.0,
  ankle_l: 2.2,
  ankle_r: 2.2,
};

/**
 * Endpoint of a segment: either a canonical landmark or a derived midpoint.
 * "mid_hip" = mean(left_hip, right_hip), "mid_shoulder" = mean(left/right_shoulder).
 */
export type SegmentEnd =
  | CanonicalPoint
  | "mid_hip"
  | "mid_shoulder"
  /** neck + 1.6·(nose − neck) — same derivation as Python normalize._canonical_points. */
  | "head_top"
  /** 0.75·foot_index + 0.25·heel (sole end point, as in Python). */
  | "foot_l"
  | "foot_r";

export interface SegmentDef {
  id: SegmentId;
  /** Joint at the proximal end (null for the root trunk segment). */
  joint: JointId | null;
  proximal: SegmentEnd;
  distal: SegmentEnd;
  /** Canonical (Python formats.py) point names of the two ends — used as contact labels. */
  proximalPoint: string;
  distalPoint: string;
  /** de Leva mass fraction of total body mass (sums to 1.0). */
  massFraction: number;
  /** COM position along proximal→distal as a fraction of segment length. */
  comRatio: number;
  /** Transverse radius of gyration as a fraction of segment length. */
  gyrationRatio: number;
  /** Parent segment id (null for root). */
  parent: SegmentId | null;
  /** Minimum plausible segment length (m) — guards degenerate/occluded poses. */
  minLength: number;
}

/**
 * Canonical segments: de Leva (1996) Table 4, male column (mass fractions sum to 1.0).
 * `comRatio` is measured from OUR proximal end: trunk 0.5690 from mid_hip (MIDS→MIDH row,
 * 1 − 0.4310), head 0.4998 from the neck (VERT→CERV row, 1 − 0.5002). `gyrationRatio` =
 * mean of the sagittal/transverse radii (trunk (0.384 + 0.358) / 2).
 */
export const SEGMENTS: SegmentDef[] = [
  {
    id: "trunk",
    joint: null,
    parent: null,
    proximal: "mid_hip",
    distal: "mid_shoulder",
    proximalPoint: "mid_hip",
    distalPoint: "mid_shoulder",
    massFraction: 0.4346,
    comRatio: 0.569,
    gyrationRatio: 0.371,
    minLength: 0.15,
  },
  {
    id: "head",
    joint: "neck",
    parent: "trunk",
    proximal: "mid_shoulder",
    distal: "head_top",
    proximalPoint: "neck",
    distalPoint: "head_top",
    massFraction: 0.0694,
    comRatio: 0.4998,
    gyrationRatio: 0.309,
    minLength: 0.08,
  },
  {
    id: "upper_arm_l",
    joint: "shoulder_l",
    parent: "trunk",
    proximal: "left_shoulder",
    distal: "left_elbow",
    proximalPoint: "shoulder_l",
    distalPoint: "elbow_l",
    massFraction: 0.0271,
    comRatio: 0.5772,
    gyrationRatio: 0.277,
    minLength: 0.08,
  },
  {
    id: "upper_arm_r",
    joint: "shoulder_r",
    parent: "trunk",
    proximal: "right_shoulder",
    distal: "right_elbow",
    proximalPoint: "shoulder_r",
    distalPoint: "elbow_r",
    massFraction: 0.0271,
    comRatio: 0.5772,
    gyrationRatio: 0.277,
    minLength: 0.08,
  },
  {
    id: "forearm_l",
    joint: "elbow_l",
    parent: "upper_arm_l",
    proximal: "left_elbow",
    distal: "left_wrist",
    proximalPoint: "elbow_l",
    distalPoint: "wrist_l",
    massFraction: 0.0162,
    comRatio: 0.4574,
    gyrationRatio: 0.2705,
    minLength: 0.07,
  },
  {
    id: "forearm_r",
    joint: "elbow_r",
    parent: "upper_arm_r",
    proximal: "right_elbow",
    distal: "right_wrist",
    proximalPoint: "elbow_r",
    distalPoint: "wrist_r",
    massFraction: 0.0162,
    comRatio: 0.4574,
    gyrationRatio: 0.2705,
    minLength: 0.07,
  },
  {
    id: "hand_l",
    joint: "wrist_l",
    parent: "forearm_l",
    proximal: "left_wrist",
    distal: "left_index",
    proximalPoint: "wrist_l",
    distalPoint: "hand_l",
    massFraction: 0.0061,
    comRatio: 0.79,
    gyrationRatio: 0.5705,
    minLength: 0.04,
  },
  {
    id: "hand_r",
    joint: "wrist_r",
    parent: "forearm_r",
    proximal: "right_wrist",
    distal: "right_index",
    proximalPoint: "wrist_r",
    distalPoint: "hand_r",
    massFraction: 0.0061,
    comRatio: 0.79,
    gyrationRatio: 0.5705,
    minLength: 0.04,
  },
  {
    id: "thigh_l",
    joint: "hip_l",
    parent: "trunk",
    proximal: "left_hip",
    distal: "left_knee",
    proximalPoint: "hip_l",
    distalPoint: "knee_l",
    massFraction: 0.1416,
    comRatio: 0.4095,
    gyrationRatio: 0.329,
    minLength: 0.15,
  },
  {
    id: "thigh_r",
    joint: "hip_r",
    parent: "trunk",
    proximal: "right_hip",
    distal: "right_knee",
    proximalPoint: "hip_r",
    distalPoint: "knee_r",
    massFraction: 0.1416,
    comRatio: 0.4095,
    gyrationRatio: 0.329,
    minLength: 0.15,
  },
  {
    id: "shank_l",
    joint: "knee_l",
    parent: "thigh_l",
    proximal: "left_knee",
    distal: "left_ankle",
    proximalPoint: "knee_l",
    distalPoint: "ankle_l",
    massFraction: 0.0433,
    comRatio: 0.4459,
    gyrationRatio: 0.252,
    minLength: 0.15,
  },
  {
    id: "shank_r",
    joint: "knee_r",
    parent: "thigh_r",
    proximal: "right_knee",
    distal: "right_ankle",
    proximalPoint: "knee_r",
    distalPoint: "ankle_r",
    massFraction: 0.0433,
    comRatio: 0.4459,
    gyrationRatio: 0.252,
    minLength: 0.15,
  },
  {
    id: "foot_l",
    joint: "ankle_l",
    parent: "shank_l",
    proximal: "left_ankle",
    distal: "foot_l",
    proximalPoint: "ankle_l",
    distalPoint: "foot_l",
    massFraction: 0.0137,
    comRatio: 0.4415,
    gyrationRatio: 0.251,
    minLength: 0.08,
  },
  {
    id: "foot_r",
    joint: "ankle_r",
    parent: "shank_r",
    proximal: "right_ankle",
    distal: "foot_r",
    proximalPoint: "ankle_r",
    distalPoint: "foot_r",
    massFraction: 0.0137,
    comRatio: 0.4415,
    gyrationRatio: 0.251,
    minLength: 0.08,
  },
];

export const SEGMENT_BY_ID = new Map<SegmentId, SegmentDef>(SEGMENTS.map((s) => [s.id, s]));

/**
 * Segments whose distal end (foot, hand, head top) can touch another person — Python
 * contacts.CONTACT_POINTS in the same order.
 */
export const CONTACT_SEGMENTS: SegmentId[] = [
  "foot_l",
  "foot_r",
  "hand_l",
  "hand_r",
  "head",
];

/**
 * Python formats.SEGMENTS order (root first, then each limb chain). The closest-segment
 * search breaks near-ties (SEGMENT_TIE_M) in favour of earlier entries, so the order matters.
 */
export const SEGMENT_SEARCH_ORDER: SegmentId[] = [
  "trunk",
  "head",
  "upper_arm_l",
  "forearm_l",
  "hand_l",
  "upper_arm_r",
  "forearm_r",
  "hand_r",
  "thigh_l",
  "shank_l",
  "foot_l",
  "thigh_r",
  "shank_r",
  "foot_r",
];

/**
 * How a segment is tested for floor contact (Python contacts.GROUND_SEGMENTS):
 * - `sole`: distal point within `allowance`; CoP anywhere on [sMin, 1].
 * - `lowest`: lower end point within `allowance`.
 * - `proximal`: proximal point within `allowance`.
 * - `flat`: both end points within `allowance`.
 * For all but `sole` the CoP is restricted to the part of the segment within `allowance`
 * (extended back to `sMin` when that part includes s = 0 and sMin < 0: the buttocks).
 */
export type GroundMode = "sole" | "lowest" | "proximal" | "flat";
export interface GroundSpec {
  segment: SegmentId;
  mode: GroundMode;
  allowance: number;
  sMin: number;
}
export const GROUND_SEGMENTS: GroundSpec[] = [
  { segment: "trunk", mode: "lowest", allowance: 0.12, sMin: -0.15 },
  { segment: "head", mode: "lowest", allowance: 0.12, sMin: 0 },
  { segment: "upper_arm_l", mode: "flat", allowance: 0.08, sMin: 0 },
  { segment: "upper_arm_r", mode: "flat", allowance: 0.08, sMin: 0 },
  { segment: "forearm_l", mode: "proximal", allowance: 0.07, sMin: 0 },
  { segment: "forearm_r", mode: "proximal", allowance: 0.07, sMin: 0 },
  { segment: "hand_l", mode: "sole", allowance: 0.06, sMin: 0 },
  { segment: "hand_r", mode: "sole", allowance: 0.06, sMin: 0 },
  { segment: "thigh_l", mode: "flat", allowance: 0.1, sMin: 0 },
  { segment: "thigh_r", mode: "flat", allowance: 0.1, sMin: 0 },
  { segment: "shank_l", mode: "proximal", allowance: 0.08, sMin: 0 },
  { segment: "shank_r", mode: "proximal", allowance: 0.08, sMin: 0 },
  { segment: "foot_l", mode: "sole", allowance: 0.06, sMin: -0.4 },
  { segment: "foot_r", mode: "sole", allowance: 0.06, sMin: -0.4 },
];

/** Landmarks required for a usable analysis (visibility ratio drives low-confidence-pose). */
export const REQUIRED_POINTS: CanonicalPoint[] = [
  "left_shoulder",
  "right_shoulder",
  "left_elbow",
  "right_elbow",
  "left_wrist",
  "right_wrist",
  "left_hip",
  "right_hip",
  "left_knee",
  "right_knee",
  "left_ankle",
  "right_ankle",
];

/** Track-format segment list (proximal/distal joint ids) for BiomechTrack.segments. */
export const TRACK_SEGMENTS = SEGMENTS.filter((s) => s.joint != null).map((s) => ({
  id: s.id,
  proximal: s.joint as JointId,
  distal: childJointOf(s.id) ?? s.id,
}));

function childJointOf(segmentId: SegmentId): JointId | null {
  const child = SEGMENTS.find((s) => s.parent === segmentId);
  return child?.joint ?? null;
}

/** Mass from explicit input → height proportions (BMI 22) → 70 kg default. */
export function resolveMass(
  massKg?: number | null,
  heightM?: number | null,
): { massKg: number; massSource: "input" | "proportions" | "default" } {
  if (massKg != null && Number.isFinite(massKg) && massKg > 10) {
    return { massKg, massSource: "input" };
  }
  if (heightM != null && Number.isFinite(heightM) && heightM > 0.5) {
    return { massKg: ASSUMED_BMI * heightM * heightM, massSource: "proportions" };
  }
  return { massKg: DEFAULT_MASS_KG, massSource: "default" };
}

/** Joint load ratio = |M| / (capacity · bodyMass). */
export function jointLoad(joint: JointId, momentMagnitude: number, massKg: number): number {
  const cap = JOINT_CAPACITY_NM_PER_KG[joint] ?? 1;
  const denom = Math.max(1e-6, cap * massKg);
  return momentMagnitude / denom;
}
